import React from 'react';
import { Box, styled, Tooltip } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';

// Horizontal stepper component for table use
const HorizontalStepperContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  width: '100%',
  minHeight: '24px',
  padding: '8px 0'
});

const StepperLine = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: 0,
  right: 0,
  height: '2px',
  backgroundColor: '#e0e0e0',
  zIndex: 1,
  transform: 'translateY(-50%)'
});

const StepperProgressLine = styled(Box)(({ progressWidth }) => ({
  position: 'absolute',
  top: '50%',
  left: 0,
  height: '2px',
  backgroundColor: '#4caf50',
  zIndex: 2,
  width: `${progressWidth}%`,
  transform: 'translateY(-50%)',
  transition: 'width 0.3s ease'
}));

const StepperDot = styled(Box)(({ status }) => {
  const isCompleted = status?.toLowerCase() === 'completed';
  const isInProgress = status?.toLowerCase() === 'in_progress' || status?.toLowerCase() === 'inprogress';
  
  return {
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    backgroundColor: isCompleted ? '#4caf50' : isInProgress ? '#ff9800' : '#e0e0e0',
    border: '2px solid #fff',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    zIndex: 3,
    position: 'relative',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    
    '&:hover': {
      transform: 'scale(1.2)',
      boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)'
    }
  };
});

// Individual step indicator for sidebar use
const SingleStepContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '22px',
  height: '22px'
});

const SingleStepDot = styled(Box)(({ status }) => {
  const isCompleted = status?.toLowerCase() === 'completed';
  const isInProgress = status?.toLowerCase() === 'in_progress' || status?.toLowerCase() === 'inprogress';
  
  return {
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    backgroundColor: isCompleted ? '#4caf50' : isInProgress ? '#ff9800' : '#e0e0e0',
    border: '2px solid #fff',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    
    '&:hover': {
      transform: 'scale(1.1)',
      boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)'
    }
  };
});

const getStatusLabel = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed':
      return 'Completed';
    case 'in_progress':
    case 'inprogress':
      return 'In Progress';
    case 'todo':
    case 'pending':
    default:
      return 'To Do';
  }
};

// Horizontal stepper for table rows
export const HorizontalTaskStepper = ({ tasks = [] }) => {
  if (!tasks || tasks.length === 0) return null;
  
  // Calculate progress width based on completed tasks
  const completedCount = tasks.filter(task => task.status?.toLowerCase() === 'completed').length;
  const totalTasks = tasks.length;
  const progressWidth = totalTasks > 0 ? (completedCount / totalTasks) * 100 : 0;
  
  return (
    <HorizontalStepperContainer>
      <StepperLine />
      <StepperProgressLine progressWidth={progressWidth} />
      {tasks.map((task, index) => (
        <Tooltip
          key={task.taskId || index}
          title={`${task.taskName || `Task ${index + 1}`}: ${getStatusLabel(task.status)}`}
          placement="top"
          arrow
        >
          <StepperDot
            status={task.status}
            sx={{
              position: 'absolute',
              left: `${(index / Math.max(tasks.length - 1, 1)) * 100}%`,
              transform: 'translateX(-50%)'
            }}
          >
            {task.status?.toLowerCase() === 'completed' && (
              <CheckIcon 
                sx={{ 
                  fontSize: '12px', 
                  color: '#fff',
                  fontWeight: 'bold'
                }} 
              />
            )}
          </StepperDot>
        </Tooltip>
      ))}
    </HorizontalStepperContainer>
  );
};

// Single step indicator for sidebar
const StatusStepper = ({ status }) => {
  return (
    <SingleStepContainer>
      <Tooltip 
        title={getStatusLabel(status)}
        placement="top"
        arrow
      >
        <SingleStepDot status={status}>
          {status?.toLowerCase() === 'completed' && (
            <CheckIcon 
              sx={{ 
                fontSize: '12px', 
                color: '#fff',
                fontWeight: 'bold'
              }} 
            />
          )}
        </SingleStepDot>
      </Tooltip>
    </SingleStepContainer>
  );
};

export default StatusStepper;